import React, { FC } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import PersonIcon from "@mui/icons-material/Person";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import logo from "../../images/logo/logo.png";
import logoMobile from "../../images/logo/logo-mobile.png";
import { paths } from "../../routes/paths";
import { tokens } from "../../styles/tokens";
import PillButton from "../ui/PillButton";

export interface AppHeaderProps {
  /** Hides the actions while auth is resolving or on the auth screens. */
  showActions?: boolean;
}

/**
 * The app's only chrome bar.
 *
 * Stays dark: the Artome logo assets are pure white on transparent, so a light
 * bar would erase the brand mark.
 *
 * Registration lives here as a persistent amber CTA, which — together with the
 * home hero — is what replaces the old text link buried in a 30px sub-bar.
 */
const AppHeader: FC<AppHeaderProps> = ({ showActions = true }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const onRegister = pathname.startsWith(paths.register);

  return (
    <Box
      component="header"
      sx={{
        bgcolor: tokens.color.ink,
        px: { mobile: 2, tablet: 4 },
        pt: "env(safe-area-inset-top)",
        position: "sticky",
        top: 0,
        zIndex: 1100,
      }}
    >
      <Box
        sx={{
          width: `min(100%, ${tokens.space.containerMax}px)`,
          mx: "auto",
          height: 64,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box
          component={RouterLink}
          to={paths.home}
          aria-label="Artome installer portal home"
          sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}
        >
          <Box
            component="img"
            src={logo}
            alt="Artome"
            sx={{ height: 26, display: { mobile: "none", tablet: "block" } }}
          />
          <Box
            component="img"
            src={logoMobile}
            alt="Artome"
            sx={{ height: 26, display: { mobile: "block", tablet: "none" } }}
          />
        </Box>

        {showActions && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              component={RouterLink}
              to={paths.guides}
              sx={{
                display: { mobile: "none", tablet: "block" },
                color: "rgba(255,255,255,0.82)",
                fontWeight: 700,
                fontSize: 15,
                textDecoration: "none",
                "&:hover": { color: "#FFFFFF" },
              }}
            >
              Installation guides
            </Box>

            {!onRegister && (
              <PillButton
                tone="amber"
                size="small"
                onClick={() => navigate(paths.registerSerial)}
              >
                Register
              </PillButton>
            )}

            <Tooltip title="Account">
              <IconButton
                onClick={() => navigate(paths.account)}
                aria-label="Account"
                sx={{
                  color: "#FFFFFF",
                  border: "1px solid rgba(255,255,255,0.24)",
                  "&:hover": { borderColor: "rgba(255,255,255,0.5)" },
                }}
              >
                <PersonIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AppHeader;
